import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import LastMessageDateEnums from 'infrastructure/enums/lastMessageDateFilterEnums';

import { getSmsDashboardMessagesKey } from './useMessages';
import { useSmsDashboardUnreadCount } from './useUnreadConversationsCount';
import { getSmsDashboardConversationsKey } from './useConversations';

import type { RootState } from 'store';
import type {
  ConversationType,
  IConversation,
  IConversationsListPages,
  IList,
  IMessagesListPages,
} from 'infrastructure/interfaces';

export interface ISocketData {
  conversationId: number;
  title: string;
  patient: {
    patientGuid: string;
    patientPhone: string;
    email: string;
    fullNameWithOnlyTitle: string;
  };
  clinic: {
    guid: string;
    legalname: string;
  };
  doctorGuid: string;
  notes: string | null;
  lastMessage: string;
  lastMessageDate: string;
  read: boolean;
  type: ConversationType;
  closed: boolean;
  spam: boolean;
  unreadMessageCount: number;
  hasPatientMessage: boolean;
  patientCurrentClinicGuid: string;
  createdAt: string;
  updatedAt: string;
  message: {
    id: string;
    authorType: string;
    authorGuid: string;
    author: {
      firstName: string;
      fullNameWithProfession: string;
      lastName: string;
      professionType: string;
      title: string;
    };
    body: string;
    status: string;
    action: string;
    read: boolean;
    from: string;
    createdAt: string;
  };
}

export const useNewMessageBroadcast = () => {
  const queryClient = useQueryClient();
  const { currentConversation, conversationType, filters } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { updateUnreadCountByType } = useSmsDashboardUnreadCount({
    canLoad: false,
  });

  const findAndRemoveConversation = (
    pages: IConversationsListPages['pages'],
    conversationId: number,
  ): [IConversation | undefined, boolean] => {
    for (const page of pages) {
      const index = page.items.findIndex((c) => c.id === conversationId);
      if (index !== -1) {
        const [conversation] = page.items.splice(index, 1);
        return [conversation, true];
      }
    }
    return [undefined, false];
  };

  const appendMessageToCache = useCallback(
    (messagesKey: any, socketData: ISocketData) => {
      queryClient.setQueryData<IMessagesListPages>(messagesKey, (old) => {
        if (!old) return old;
        const { message } = socketData;
        const newMessage = {
          ...old.pages[0].items[0],
          ...message,
          messageId: message.id,
          createdAt: new Date(message.createdAt),
          updatedAt: new Date(message.createdAt),
          readAt: new Date(message.createdAt),
        };
        return {
          ...old,
          pages: [
            { ...old.pages[0], items: [newMessage, ...old.pages[0].items] },
            ...old.pages.slice(1),
          ],
        };
      });
    },
    [queryClient],
  );

  const newMessageBroadcast = useCallback(
    (socketData: ISocketData, needAddConversation: boolean) => {
      const isPatientMessage = socketData.message.authorType === 'patient';
      const hasOneUnread = socketData.unreadMessageCount === 1;
      const isDifferentConversationType = conversationType !== socketData.type;
      const isOldestFilter =
        filters.lastMessageDate === LastMessageDateEnums.OLDEST;

      if (isDifferentConversationType) {
        if (isPatientMessage && hasOneUnread) {
          updateUnreadCountByType(socketData.type, 1);
        }
        return;
      }

      const conversationKey = getSmsDashboardConversationsKey({
        type: conversationType,
        read: filters.read,
        closed: filters.closed,
        lastMessageDate: filters.lastMessageDate,
        searchQuery: filters.searchQuery,
      });

      const messagesKey = getSmsDashboardMessagesKey({
        conversationId: socketData.conversationId,
        nextCursor: undefined,
        prevCursor: undefined,
        searchQuery: '',
        searchQueryDate: '',
      });

      const isCurrentConversation =
        currentConversation.id === socketData.conversationId;

      queryClient.setQueriesData(
        { queryKey: [conversationKey[0]] },
        (oldData: IConversationsListPages | undefined) => {
          if (!oldData) return oldData;

          const pages = structuredClone(oldData?.pages ?? []);

          const [foundConversation, conversationRemoved] =
            findAndRemoveConversation(pages, socketData.conversationId);

          if (foundConversation?.closed !== socketData?.closed) {
            queryClient.invalidateQueries([conversationKey[0]]);
          }

          if (isPatientMessage && hasOneUnread) {
            const delta =
              socketData.unreadMessageCount -
              (foundConversation?.unreadMessageCount || 0);
            updateUnreadCountByType(conversationType, delta);
          }

          if (!conversationRemoved && isPatientMessage) {
            updateUnreadCountByType(
              conversationType,
              socketData.unreadMessageCount,
            );
          }

          if (needAddConversation) {
            const newConversation: IConversation = {
              ...(foundConversation ?? ({} as any)),
              ...socketData,
              id: socketData.conversationId,
              lastMessageDate: socketData.updatedAt,
            };

            const lastPage: IList | undefined = pages[pages?.length - 1];

            if (!isOldestFilter && pages[0]) {
              pages[0].items.unshift(newConversation);
            } else if (
              isOldestFilter &&
              conversationRemoved &&
              !lastPage.hasMore &&
              pages.length > 0
            ) {
              pages[pages.length - 1].items.push(newConversation);
            }
            return {
              ...oldData,
              pages,
            };
          }

          return oldData;
        },
      );

      if (isCurrentConversation) {
        appendMessageToCache(messagesKey, socketData);
      }
    },
    [
      queryClient,
      currentConversation.id,
      conversationType,
      filters,
      updateUnreadCountByType,
    ],
  );

  return { newMessageBroadcast };
};
